import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, X } from "lucide-react";
import { ALL_CITY_SLUGS, CURATED, PROGRAMMATIC } from "../../data/cityCatalogue";

const DISMISS_KEY = "bbc-geo-pill-dismissed";

/* Common aliases returned by IP-geo services that don't match our slugs 1:1 */
const CITY_ALIASES = {
  bangalore: "bengaluru",
  bombay: "mumbai",
  calcutta: "kolkata",
  madras: "chennai",
  "new delhi": "delhi",
  gurugram: "gurgaon",
  trivandrum: "thiruvananthapuram",
  calicut: "kozhikode",
  pondicherry: "puducherry",
  bengaluru: "bengaluru",
  "greater noida": "greater-noida",
  "navi mumbai": "navi-mumbai",
  "hubli-dharwad": "hubli",
  hubballi: "hubli",
  belagavi: "belgaum",
  mysore: "mysuru",
  "chhatrapati sambhajinagar": "aurangabad",
  allahabad: "prayagraj",
  "bokaro steel city": "bokaro",
  mangalore: "mangalore",
  mangaluru: "mangalore",
  panjim: "panaji",
  panjimargao: "margao",
};

const normalise = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");

const findMatchingSlug = (cityName) => {
  if (!cityName) return null;
  const n = normalise(cityName);
  const key = n.replace(/\s+/g, "-");
  if (CITY_ALIASES[n]) return CITY_ALIASES[n];
  if (ALL_CITY_SLUGS.includes(key)) return key;
  if (ALL_CITY_SLUGS.includes(n.replace(/\s+/g, ""))) return n.replace(/\s+/g, "");
  // last resort: substring match against curated + programmatic names
  const catalogue = [...Object.values(CURATED), ...PROGRAMMATIC];
  const hit = catalogue.find((c) => normalise(c.name) === n);
  return hit ? hit.slug : null;
};

/*
 * HeroCityPill — silent IP-geolocation (ipapi.co, no key, HTTPS, ~30k free/mo).
 * Shows a small "See BBC in {your city} →" pill under the hero eyebrow when
 * the detected city has a matching /boom-barrier-{slug} page. Fails silent.
 * Dismissable — remembered via localStorage.
 */
export const HeroCityPill = () => {
  const [state, setState] = useState({ city: null, slug: null, dismissed: false });

  useEffect(() => {
    let cancelled = false;
    try {
      if (typeof window !== "undefined" && window.localStorage.getItem(DISMISS_KEY) === "1") {
        setState((s) => ({ ...s, dismissed: true }));
        return () => {};
      }
    } catch (_) { /* localStorage blocked — carry on */ }

    const controller = new AbortController();
    fetch("https://ipapi.co/json/", { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (cancelled || !j) return;
        if (j.country_code && j.country_code !== "IN") return; // scope: India-only pill
        const slug = findMatchingSlug(j.city);
        if (slug) setState({ city: j.city, slug, dismissed: false });
      })
      .catch(() => { /* geo fail — do nothing, graceful */ });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const dismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { window.localStorage.setItem(DISMISS_KEY, "1"); } catch (_) {}
    setState((s) => ({ ...s, dismissed: true }));
  };

  if (state.dismissed || !state.slug) return null;

  return (
    <Link
      to={`/boom-barrier-${state.slug}`}
      data-testid="hero-geo-pill"
      className="group mt-6 inline-flex items-center gap-2 pl-3 pr-1 py-1.5 bg-[#00FF85]/10 border border-[#00FF85]/40 hover:border-[#00FF85] hover:bg-[#00FF85]/15 transition font-mono text-[10px] uppercase tracking-[0.22em] text-[#00FF85]"
    >
      <MapPin size={11} />
      <span>See BBC in {state.city}</span>
      <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="hero-geo-pill-dismiss"
        className="ml-1 w-5 h-5 flex items-center justify-center rounded-sm text-[#00FF85]/60 hover:text-[#00FF85] hover:bg-white/5"
      >
        <X size={11} />
      </button>
    </Link>
  );
};

export default HeroCityPill;
